/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useState } from 'react';

import { TextField, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { getPercentGradeFromDecimal } from 'bundles/author-common/utils/GradeUtils';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import ControlsDialog from 'bundles/grading/components/staff-graded-assignments/grade-override/ControlsDialog';
import ErrorNotification from 'bundles/grading/components/staff-graded-assignments/grade-override/ErrorNotification';
import useApplyGradeOverrideMutation from 'bundles/grading/hooks/useApplyGradeOverrideMutation';
import { getGradePercentNumberForDisplay } from 'bundles/grading/utils/gradeUtils';

import _t from 'i18n!nls/grading';

const styles = {
  verticalAlignRow: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  `,
  arrowIcon: css`
    margin: 0 var(--cds-spacing-200);
    flex-grow: 0;
    align-self: flex-end;
    margin-bottom: var(--cds-spacing-150);
  `,
  overrideInputContainer: css`
    position: relative;
    flex-grow: 1;
  `,

  commentTextField: css`
    margin-top: var(--cds-spacing-300);
  `,
  exceeds100Percent: css`
    position: absolute;
    width: 100%;
    text-align: right;
    padding: var(--cds-spacing-50) var(--cds-spacing-100) 0 0;
  `,
};

type Props = {
  onClose: () => void;
};

// Component for dialog to apply grade override
const ApplyGradeOverrideDialog = ({ onClose }: Props) => {
  const { submissionData } = useContext(SGAPageContext);
  const { applyGradeOverride, loading, error } = useApplyGradeOverrideMutation();

  const { gradebookEntry } = submissionData.data || {};
  // default states for initial and resetting form values
  // callback usage ensures latest data is fetched when resetting
  const defaultStates = {
    overrideGradePercentValue: () =>
      gradebookEntry?.overrideOutcome?.grade !== undefined
        ? getGradePercentNumberForDisplay(gradebookEntry.overrideOutcome.grade * 100)
        : undefined,
    overrideGradePercentValueStr: () =>
      gradebookEntry?.overrideOutcome?.grade !== undefined
        ? getGradePercentNumberForDisplay(gradebookEntry.overrideOutcome.grade * 100).toString()
        : '',
    commentForLearner: () => gradebookEntry?.overrideOutcome?.explanation || '',
    commentForCourseStaff: () => gradebookEntry?.overrideOutcome?.partnerExplanation || '',
  };

  const [overrideGradePercentValue, setOverrideGradePercentValue] = useState(defaultStates.overrideGradePercentValue());
  const [overrideGradePercentValueStr, setOverrideGradePercentValueStr] = useState(
    defaultStates.overrideGradePercentValueStr()
  );

  const [commentForLearner, setCommentForLearner] = useState(defaultStates.commentForLearner());
  const [commentForCourseStaff, setCommentForCourseStaff] = useState(defaultStates.commentForCourseStaff());

  const [showValidation, setShowValidation] = useState(false);

  // handles the input change from user and parses appropriately
  const handleOverrideGradeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // ensure input is an acceptable float number and update the states associated
    if (
      !isNaN(parseFloat(event.target.value)) &&
      parseFloat(event.target.value) >= 0 &&
      parseFloat(event.target.value) <= 1000
    ) {
      setOverrideGradePercentValue(getGradePercentNumberForDisplay(parseFloat(event.target.value))); // capped to 2 decimal places
      setOverrideGradePercentValueStr(event.target.value);
    }
    // otherwise if it's an empty string, simply use 0 in input
    else if (event.target.value === '') {
      setOverrideGradePercentValue(0);
      setOverrideGradePercentValueStr(event.target.value);
    }
  };

  // format the string input with the correct float-parsed value after user clicks away
  // this is to remove leading 0's and other malformed number-string relations
  const handleOverrideGradeInputBlur = useCallback<() => $TSFixMe>(() => {
    setOverrideGradePercentValueStr(overrideGradePercentValue?.toString() || '');
  }, [overrideGradePercentValue]);

  const overrideGradeIsValid =
    overrideGradePercentValue !== undefined && overrideGradePercentValue !== defaultStates.overrideGradePercentValue();

  // applies grade override
  const handleOverrideGradeClick = async () => {
    // ensure changes are required for override to begin with before applying
    if (overrideGradeIsValid) {
      setShowValidation(false);
      await applyGradeOverride({
        grade: (overrideGradePercentValue || 0) / 100,
        explanation: commentForLearner,
        partnerExplanation: commentForCourseStaff,
      });
      onClose();
    } else {
      setShowValidation(true);
    }
  };

  return (
    <ControlsDialog
      onClose={onClose}
      heading={_t('Override grade')}
      primaryButtonLabel={_t('Confirm')}
      primaryButtonDisabled={loading}
      primaryButtonOnClick={handleOverrideGradeClick}
      applyingOnClickAction={loading}
      secondaryButtonDisabled={loading}
      dataTestidPrefix="grade-override-dialog"
    >
      <Typography2 variant="bodyPrimary" component="div" css={styles.verticalAlignRow}>
        <div css={styles.overrideInputContainer}>
          <TextField
            type="number"
            label={_t('Original grade')}
            maxLength={4}
            suffix="%"
            readOnly
            disabled
            fullWidth
            value={
              gradebookEntry?.originalOutcome?.grade
                ? getPercentGradeFromDecimal(gradebookEntry.originalOutcome.grade)
                : ''
            }
          />
        </div>

        <ArrowNextIcon size="large" css={styles.arrowIcon} />
        <div css={styles.overrideInputContainer}>
          <TextField
            type="number"
            inputProps={{ inputMode: 'numeric' }}
            label={_t('Override grade')}
            maxLength={4}
            suffix="%"
            fullWidth
            value={overrideGradePercentValueStr}
            onChange={handleOverrideGradeInputChange}
            onBlur={handleOverrideGradeInputBlur}
            necessityIndicator="icon"
            validationLabel={_t('This field is required')}
            validationStatus={showValidation && !overrideGradeIsValid ? 'error' : undefined}
          />
          {overrideGradePercentValue !== undefined && overrideGradePercentValue > 100 && (
            <Typography2 component="p" variant="bodySecondary" css={styles.exceeds100Percent}>
              {_t('Grade exceeds 100%')}
            </Typography2>
          )}
        </div>
      </Typography2>
      <TextField
        css={styles.commentTextField}
        multiline
        label={_t('Note for learner')}
        placeholder={_t('Leave a comment...')}
        supportText={_t('These comments will appear for learners on their grades page.')}
        optional
        fullWidth={true}
        maxRows={6}
        value={commentForLearner}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCommentForLearner(event.target.value)}
        disabled={loading}
      />

      <TextField
        css={styles.commentTextField}
        multiline
        // eslint-disable-next-line no-restricted-syntax
        label={_t('Note for course staff')}
        placeholder={_t('Leave a comment...')}
        supportText={_t('These comments will appear for staff in the grading change log.')}
        optional
        fullWidth={true}
        maxRows={6}
        value={commentForCourseStaff}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCommentForCourseStaff(event.target.value)}
        disabled={loading}
      />
      {error && <ErrorNotification isApplyOverrideDialog={true} errorDetails={error.message} />}
    </ControlsDialog>
  );
};

export default ApplyGradeOverrideDialog;
